import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Crown, Plus, Minus, Copy, RefreshCcw, Trash2, ShieldAlert, Building2,
  Users, GraduationCap, Wallet, TrendingUp, Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

const zar = (n) => `R ${Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`;
const err = (e) => formatApiError(e.response?.data?.detail) || e.message;

const EMPTY = {
  name: "", principal_email: "", principal_name: "",
  licence_learners: 300, licence_teachers: 30, licence_staff: 20,
  monthly_rate: 19, notes: "",
};

function Stepper({ value, onChange, step = 10, min = 0, testid }) {
  return (
    <div className="flex items-center gap-1 mt-1.5">
      <Button
        type="button" size="icon" variant="outline"
        className="h-10 w-10 rounded-xl shrink-0"
        onClick={() => onChange(Math.max(min, Number(value) - step))}
        data-testid={`${testid}-dec`}
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <Input
        type="number" min={min}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="rounded-xl h-10 text-center tabular-nums"
        data-testid={testid}
      />
      <Button
        type="button" size="icon" variant="outline"
        className="h-10 w-10 rounded-xl shrink-0"
        onClick={() => onChange(Math.max(min, Number(value) + step))}
        data-testid={`${testid}-inc`}
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// First-login setup: change password + activate TOTP.
// Shown as a full-screen modal until BOTH are done.
// -----------------------------------------------------------------------------
function OwnerSetupWizard({ me, onDone }) {
  const [step, setStep] = useState(me.must_change_password ? "password" : (me.totp_activated ? "done" : "totp"));
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [prov, setProv] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (step === "totp" && !prov) {
      api.get("/owner/totp/provisioning")
        .then((r) => setProv(r.data))
        .catch((e) => toast.error(err(e)));
    }
  }, [step, prov]);

  const submitPw = async () => {
    if (newPw.length < 12) return toast.error("Password must be ≥ 12 characters");
    if (newPw !== confirmPw) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/owner/password/change", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setStep(me.totp_activated ? "done" : "totp");
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const submitTotp = async () => {
    setBusy(true);
    try {
      await api.post("/owner/totp/verify", { code: totpCode.trim() });
      toast.success("TOTP activated — you'll need this every login from now on");
      setStep("done");
      setTimeout(onDone, 400);
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={step !== "done"} onOpenChange={() => {}}>
      <DialogContent
        className="rounded-2xl max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="owner-setup-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            Secure your owner account
          </DialogTitle>
          <DialogDescription>
            Two quick steps before we let you into the console. You only do this once.
          </DialogDescription>
        </DialogHeader>

        {step === "password" && (
          <div className="space-y-3 py-2" data-testid="owner-setup-password">
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-primary">Step 1 · Change your password</div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Current password (from your env)</Label>
              <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="owner-current-pw" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">New password (≥ 12 chars)</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="owner-new-pw" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm new password</Label>
              <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                     className="rounded-xl mt-1.5" data-testid="owner-confirm-pw" />
            </div>
            <DialogFooter>
              <Button onClick={submitPw} disabled={busy} className="rounded-xl gap-1" data-testid="owner-submit-pw">
                {busy ? "Saving…" : "Continue"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "totp" && (
          <div className="space-y-3 py-2" data-testid="owner-setup-totp">
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-primary">Step 2 · Activate TOTP</div>
            <p className="text-sm text-muted-foreground">
              Open Google Authenticator (or 1Password / Authy). Add a new account, choose "Enter setup key",
              and paste the secret below. Then enter the 6-digit code your app shows.
            </p>
            {prov && (
              <div className="p-3 rounded-xl bg-muted/40 font-mono text-xs break-all border border-border/60"
                   data-testid="owner-totp-secret">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1">
                  Setup key
                </div>
                {prov.secret}
                <Button size="sm" variant="ghost" className="ml-2 h-6 px-2"
                        onClick={() => { navigator.clipboard.writeText(prov.secret); toast.success("Secret copied"); }}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">6-digit code</Label>
              <Input value={totpCode} onChange={(e) => setTotpCode(e.target.value)}
                     inputMode="numeric" maxLength={6}
                     placeholder="123456"
                     className="rounded-xl mt-1.5 text-center text-2xl tracking-widest font-mono"
                     data-testid="owner-totp-code" />
            </div>
            <DialogFooter>
              <Button onClick={submitTotp} disabled={busy || totpCode.length !== 6}
                      className="rounded-xl gap-1" data-testid="owner-submit-totp">
                {busy ? "Verifying…" : "Activate TOTP"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Add-school form (reusable for create + edit)
// -----------------------------------------------------------------------------
function SchoolForm({ form, setForm, isEdit }) {
  const monthlyTotal = (form.licence_learners || 0) * (form.monthly_rate || 0);
  return (
    <div className="grid gap-4 md:grid-cols-2 py-2">
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">School name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
               placeholder="Elysiom College" className="rounded-xl mt-1.5" data-testid="owner-school-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Principal email</Label>
        <Input type="email" value={form.principal_email}
               onChange={(e) => setForm({ ...form, principal_email: e.target.value })}
               placeholder="principal@school.co.za"
               className="rounded-xl mt-1.5" disabled={isEdit}
               data-testid="owner-school-principal-email" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Principal name</Label>
        <Input value={form.principal_name} onChange={(e) => setForm({ ...form, principal_name: e.target.value })}
               placeholder="Dr. Thandiwe Mokoena" className="rounded-xl mt-1.5"
               data-testid="owner-school-principal-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <GraduationCap className="h-3 w-3" /> Learners licence
        </Label>
        <Stepper value={form.licence_learners} step={25}
                 onChange={(v) => setForm({ ...form, licence_learners: v })}
                 testid="owner-school-learners" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Users className="h-3 w-3" /> Teachers licence
        </Label>
        <Stepper value={form.licence_teachers} step={5}
                 onChange={(v) => setForm({ ...form, licence_teachers: v })}
                 testid="owner-school-teachers" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Users className="h-3 w-3" /> Other staff licence
        </Label>
        <Stepper value={form.licence_staff} step={5}
                 onChange={(v) => setForm({ ...form, licence_staff: v })}
                 testid="owner-school-staff" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
          <Wallet className="h-3 w-3" /> Rate per learner / month (R)
        </Label>
        <Input type="number" step="0.5" value={form.monthly_rate}
               onChange={(e) => setForm({ ...form, monthly_rate: Number(e.target.value) })}
               className="rounded-xl mt-1.5" data-testid="owner-school-rate" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
        <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-xl mt-1.5" rows={2}
                  placeholder="Anything internal you want to remember about this school…" />
      </div>
      <div className="md:col-span-2 p-3 rounded-xl bg-primary/10 border border-primary/25 text-sm flex items-center justify-between">
        <span>Contracted monthly revenue</span>
        <span className="font-bold tabular-nums" data-testid="owner-school-monthly-total">
          {zar(monthlyTotal)} / mo
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Owner Console
// -----------------------------------------------------------------------------
export default function OwnerConsole() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [schools, setSchools] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [justCreated, setJustCreated] = useState(null); // { invite_url }

  const load = async () => {
    const [m, s, sum] = await Promise.all([
      api.get("/owner/me"),
      api.get("/owner/schools").catch(() => ({ data: [] })),
      api.get("/owner/summary").catch(() => ({ data: null })),
    ]);
    setMe(m.data);
    setSchools(s.data);
    setSummary(sum.data);
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "owner") return <Navigate to="/dashboard" replace />;
  if (!me) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const needsSetup = me.must_change_password || !me.totp_activated;

  const submitCreate = async () => {
    try {
      const { data } = await api.post("/owner/schools", form);
      toast.success(`${data.name} added — invite ready to send`);
      setForm(EMPTY);
      setCreating(false);
      setJustCreated({ name: data.name, invite_url: data.invite_url });
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const submitEdit = async () => {
    try {
      const payload = {
        name: form.name, principal_name: form.principal_name,
        licence_learners: form.licence_learners, licence_teachers: form.licence_teachers,
        licence_staff: form.licence_staff, monthly_rate: form.monthly_rate,
        notes: form.notes || "",
      };
      await api.patch(`/owner/schools/${editing.id}`, payload);
      toast.success("School updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const changeStatus = async (school, status) => {
    try {
      await api.patch(`/owner/schools/${school.id}`, { status });
      toast.success(`${school.name} · ${status}`);
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const doDelete = async (school) => {
    try {
      await api.delete(`/owner/schools/${school.id}`);
      toast.success(`Removed ${school.name}`);
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const reinvite = async (school) => {
    try {
      const { data } = await api.post(`/owner/schools/${school.id}/reinvite`);
      setJustCreated({ name: school.name, invite_url: data.invite_url });
      toast.success("Fresh invite generated");
      load();
    } catch (e) { toast.error(err(e)); }
  };

  const openEdit = (s) => {
    setForm({
      name: s.name, principal_email: s.principal_email, principal_name: s.principal_name,
      licence_learners: s.licence_learners, licence_teachers: s.licence_teachers,
      licence_staff: s.licence_staff, monthly_rate: s.monthly_rate,
      notes: s.notes || "",
    });
    setEditing(s);
  };

  const kpis = summary ? [
    { l: "Schools", v: summary.schools_active, hint: `${summary.schools_pending} pending` },
    { l: "Contracted learners", v: summary.contracted_learners.toLocaleString("en-ZA"), hint: `${summary.contracted_teachers} teachers` },
    { l: "MRR", v: zar(summary.mrr_zar), hint: `${zar(summary.arr_zar)} ARR` },
    { l: "Contracted staff", v: summary.contracted_staff, hint: `across ${summary.schools_active} schools` },
  ] : [];

  return (
    <div className="space-y-6" data-testid="owner-console">
      {needsSetup && <OwnerSetupWizard me={me} onDone={load} />}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[0.65rem] tracking-[0.2em] uppercase text-amber-500">Super owner · Restricted</div>
          <h1 className="text-4xl font-bold tracking-tight flex items-center gap-3">
            <Crown className="h-8 w-8 text-amber-500" /> Owner Console
          </h1>
          <p className="text-muted-foreground text-sm mt-1 font-serif-display">
            Onboard schools, set licences, watch the recurring revenue.
          </p>
        </div>
        <Badge className="rounded-full bg-amber-500/15 text-amber-500 hover:bg-amber-500/25 border-amber-500/30">
          <Check className="h-3 w-3 mr-1" /> {me.email}
        </Badge>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.l} className="rounded-2xl" data-testid={`owner-kpi-${k.l.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-6">
              <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">{k.l}</div>
              <div className="mt-2 text-3xl font-bold tracking-tight">{k.v}</div>
              <div className="mt-1 text-xs text-muted-foreground">{k.hint}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Schools list */}
      <Card className="rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" /> Onboarded schools
          </CardTitle>
          <Button onClick={() => { setForm(EMPTY); setCreating(true); }}
                  className="rounded-xl gap-1" data-testid="owner-new-school">
            <Plus className="h-4 w-4" /> Onboard school
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border/60">
            <div className="grid grid-cols-[2fr,1.6fr,1fr,1fr,1fr,auto] gap-3 px-4 py-3 text-[0.65rem] tracking-widest uppercase text-muted-foreground">
              <div>School / principal</div><div>Licence caps</div>
              <div>Rate</div><div>MRR</div><div>Status</div><div>Actions</div>
            </div>
            {schools.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No schools yet. Click <b>Onboard school</b> to whitelist your first principal.
              </div>
            )}
            {schools.map((s) => (
              <div key={s.id} className="grid grid-cols-[2fr,1.6fr,1fr,1fr,1fr,auto] gap-3 px-4 py-3 items-center"
                   data-testid={`owner-school-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.principal_name} · {s.principal_email}</div>
                </div>
                <div className="text-sm space-y-0.5">
                  <div className="flex items-center gap-1"><GraduationCap className="h-3 w-3 text-muted-foreground" /> {s.licence_learners} learners</div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" /> {s.licence_teachers} teachers · {s.licence_staff} staff
                  </div>
                </div>
                <div className="text-sm">R {s.monthly_rate}/mo</div>
                <div className="text-sm tabular-nums font-medium">{zar(s.licence_learners * s.monthly_rate)}</div>
                <div>
                  <Select value={s.status} onValueChange={(v) => changeStatus(s, v)}>
                    <SelectTrigger className={cn("rounded-full h-7 text-xs w-28 capitalize",
                      s.status === "active" && "bg-emerald-500/15 text-emerald-500 border-emerald-500/25",
                      s.status === "pending" && "bg-amber-500/15 text-amber-500 border-amber-500/25",
                      s.status === "suspended" && "bg-red-500/15 text-red-500 border-red-500/25")}
                      data-testid={`owner-school-status-${s.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(s)}
                          data-testid={`owner-school-edit-${s.id}`}>
                    <TrendingUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => reinvite(s)}
                          data-testid={`owner-school-reinvite-${s.id}`}>
                    <RefreshCcw className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="text-red-500"
                              data-testid={`owner-school-delete-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Revoke licence for {s.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This deletes the school licence and invalidates any pending invite URL.
                          Existing users at this school are not deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => doDelete(s)}
                                           className="rounded-xl bg-red-500 hover:bg-red-600">
                          Revoke licence
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>Onboard a new school</DialogTitle>
            <DialogDescription>
              Whitelists the principal's email and generates a one-off registration link. When they open it,
              their School OS admin account is created — pre-linked to this licence.
            </DialogDescription>
          </DialogHeader>
          <SchoolForm form={form} setForm={setForm} isEdit={false} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitCreate}
                    disabled={!form.name || !form.principal_email || !form.principal_name}
                    className="rounded-xl gap-1" data-testid="owner-create-submit">
              Onboard & generate invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit {editing?.name}</DialogTitle>
            <DialogDescription>
              Update licence caps or notes. Principal email is fixed — to move to a new principal, revoke the licence and onboard afresh.
            </DialogDescription>
          </DialogHeader>
          <SchoolForm form={form} setForm={setForm} isEdit={true} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={submitEdit} className="rounded-xl" data-testid="owner-edit-submit">
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite URL preview */}
      <Dialog open={!!justCreated} onOpenChange={(v) => { if (!v) setJustCreated(null); }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="owner-invite-modal">
          <DialogHeader>
            <DialogTitle>Invite ready · {justCreated?.name}</DialogTitle>
            <DialogDescription>
              Copy this link and send it to the principal via WhatsApp, email or Slack. It expires in 30 days.
              (Once Resend is wired we'll email it automatically.)
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-xl bg-muted/40 border border-border/60 font-mono text-xs break-all"
               data-testid="owner-invite-url">
            {justCreated?.invite_url}
          </div>
          <DialogFooter>
            <Button
              onClick={() => { navigator.clipboard.writeText(justCreated.invite_url); toast.success("Copied"); }}
              className="rounded-xl gap-1" data-testid="owner-invite-copy"
            >
              <Copy className="h-4 w-4" /> Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
